import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { wurBlue, headerBackground } from '../../style/colors';
import { guestKey, modelVersions } from '../../services/constants';
// import wur from '../../assets/wur-logo.png';
import wateringCan from '../../assets/logos/watering-can.png';
import grodan from '../../assets/logos/grodan.jpg';
import jiffy from '../../assets/logos/jiffy.png';
import yara from '../../assets/logos/yara.png';
import eurofins from '../../assets/logos/eurofins.jpg';
import { useParams } from 'react-router-dom';
import IconKey from '../Icons/IconKey';
import { useEffect, useRef, useState } from 'react';
import useStore, { actions } from '../../store/useStore';
import { loadingStates } from '../../store/constants';
import { changeKey } from '../../store/asyncActions';
import Checkbox from '../Input/Checkbox';
import { useLocalStorage } from '../../store/helpers';

const MainContainer = styled.header`
    background: ${headerBackground};
    padding: 4px 5%;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TitleBlock = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
`;

const Title = styled.h1`
    color: ${wurBlue};
    font-size: 1.4rem;
`;

const RightBlock = styled.div`
    align-self: stretch;
    text-align: right;
`;

const keyBlockWidth = 300;

const KeyBlock = styled.div`
    white-space: nowrap;

    svg {
        display: inline-block;
        vertical-align: middle;
    }
`;

const KeyIconButton = styled.div`
    display: inline-block;
    vertical-align: middle;
    font-size: 36px;
    cursor: pointer;
`;

const KeyActionButton = styled.button.attrs({ tabIndex: 0 })`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    margin-left: 4px;
    margin-right: 4px;
    cursor: pointer;
    user-select: none;
`;

const KeyInput = styled.input`
    width: ${keyBlockWidth - 50}px; // buttons and margin
    box-sizing: border-box;
    margin-left: 4px;
    margin-right: 4px;
`;

const StoreKeyOnComputerCheckbox = styled(Checkbox)`
    font-size: 0.7rem;
`;

const KeyInputBlock = styled.div`
    width: ${(props) => (props.open ? keyBlockWidth : 0)}px;
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;
    transition: width 500ms;
    text-align: left;
    white-space: nowrap;
`;

const SwitchModelLink = styled(Link)`
    text-decoration: underline;
    font-size: 80%;
    cursor: pointer;
    color: ${wurBlue};

    &:visited {
        color: ${wurBlue};
    }
`;

// const WateringCan = styled.img.attrs({
//     src: process.env.PUBLIC_URL + '/favicon-32x32.png',
// })`
//     vertical-align: middle;
//     margin-right: 16px;
// `;

const LogoImg = styled.img`
    max-height: 100px;
    max-width: 240px;
    /* mix-blend-mode: darken; */
`;

const logos = {
    grodan,
    jiffy,
    yara,
    ebab: wateringCan,
    eurofins,
};

const Header = ({ modelVersion }) => {
    const params = useParams();
    const { company = 'ebab' } = params || {};
    const { storeValue, removeItem } = useLocalStorage();
    const { store, dispatch } = useStore();
    const [keyOpen, setKeyOpen] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [keyInputValue, setKeyInputValue] = useState(store?.key ?? '');
    const [storeOnComputer, setStoreOnComputer] = useState(false);
    const resetTimer = useRef(null);
    const closeTimer = useRef(null);

    const { storeKeyInBrowser } = store;

    const submitting = [
        loadingStates.initializing,
        loadingStates.fetching,
    ].includes(store.loadingState);

    const scheduleClose = (time = 500) => {
        clearTimers(closeTimer);
        closeTimer.current = setTimeout(() => {
            setKeyOpen(false);
        }, time * 10);
    };

    const toggleKeyOpen = () => {
        clearTimers(closeTimer);
        setKeyOpen((prev) => {
            if (
                prev &&
                (store.key !== keyInputValue ||
                    store.loadingState === loadingStates.error)
            ) {
                return prev;
            }
            return !prev;
        });
    };

    const handleChangeKey = (changeEvent) => {
        const value = changeEvent.target.value;
        setKeyInputValue(value);
    };

    const clearTimers = (...handleRefs) => {
        handleRefs.forEach((handleRef) => {
            if (handleRef.current) {
                clearTimeout(handleRef.current);
                handleRef.current = null;
            }
        });
    };

    const cancelKey = () => {
        clearTimers(resetTimer, closeTimer);
        if (
            keyInputValue === store.key
            // && store.loadingState === loadingStates.error
        ) {
            changeKey(store, dispatch, guestKey).then((keyChangeResult) => {
                console.log('keyChangeResult:', keyChangeResult);
                if (keyChangeResult) {
                    scheduleClose();
                }
            });
        } else {
            setKeyInputValue(store.key);
            scheduleClose();
        }
    };

    const submitKey = () => {
        clearTimers(resetTimer, closeTimer);
        const nextKey = keyInputValue || guestKey;
        changeKey(store, dispatch, nextKey).then((keyChangeResult) => {
            if (keyChangeResult) {
                if (storeKeyInBrowser) {
                    storeValue('key', nextKey);
                }
                scheduleClose();
            }
        });
    };

    const onFocusKey = () => {
        clearTimers(resetTimer, closeTimer);
        setHasFocus(true);
    };
    const onBlurKey = (blurEvent) => {
        const pushedButton = blurEvent.relatedTarget?.id;
        if (
            !pushedButton ||
            (pushedButton !== 'submitKeyButton' &&
                pushedButton !== 'cancelKeyButton')
        ) {
            if (store.key === keyInputValue) {
                scheduleClose(5000);
            } else {
                resetTimer.current = setTimeout(cancelKey, 5000);
            }
        }
        setHasFocus(false);
    };

    useEffect(() => {
        return () => {
            clearTimers(resetTimer, closeTimer);
        };
    }, []);

    useEffect(() => {
        setKeyInputValue(store.key);
        clearTimers(resetTimer);
    }, [store.key]);

    useEffect(() => {
        if (
            store.loadingState === loadingStates.error &&
            store.key !== guestKey
        ) {
            setKeyOpen(true);
        }
        if (store.loadingState === loadingStates.success && !hasFocus) {
            clearTimers(closeTimer, resetTimer);
            scheduleClose();
        }
    }, [store.loadingState, store.key]);

    const handleChangeSaveOnComputer = (nextVal) => {
        if (store.key === keyInputValue) {
            if (nextVal) {
                storeValue('key', store.key);
            } else {
                removeItem('key');
            }
        }
        dispatch(actions.setStoreKeyInBrowser(nextVal));
    };

    return (
        <MainContainer>
            <TitleBlock>
                <LogoImg src={logos[company]} alt={`${company} logo`} />
                <Title>Bemestingsadviesmodel {modelVersion}</Title>
            </TitleBlock>
            <RightBlock className="dont-print">
                <KeyBlock>
                    <KeyIconButton onClick={toggleKeyOpen}>
                        <IconKey />
                    </KeyIconButton>
                    <KeyInputBlock open={keyOpen}>
                        {keyOpen && (
                            <KeyInput
                                value={keyInputValue}
                                onChange={handleChangeKey}
                                onFocus={onFocusKey}
                                onBlur={onBlurKey}
                                disabled={submitting}
                                autoFocus
                            />
                        )}
                        {(store.key !== keyInputValue ||
                            store.loadingState === loadingStates.error) && (
                            <KeyActionButton
                                style={{ color: 'green' }}
                                onClick={submitKey}
                                id="submitKeyButton"
                                disabled={submitting}
                            >
                                &#10004;
                            </KeyActionButton>
                        )}
                        {(store.key !== keyInputValue ||
                            store.key !== guestKey ||
                            store.loadingState === loadingStates.error) && (
                            <KeyActionButton
                                style={{ color: 'red' }}
                                onClick={cancelKey}
                                id="cancelKeyButton"
                                disabled={submitting}
                            >
                                &#x2718;
                            </KeyActionButton>
                        )}
                        {keyOpen && (
                            <StoreKeyOnComputerCheckbox
                                label="Bewaren op deze computer"
                                value={storeKeyInBrowser}
                                onChange={handleChangeSaveOnComputer}
                            />
                        )}
                    </KeyInputBlock>
                </KeyBlock>
                <SwitchModelLink
                    to={
                        modelVersion === modelVersions.substraat
                            ? '../potplanten'
                            : '../substraatteelt'
                    }
                >
                    naar{' '}
                    {modelVersion === modelVersions.substraat
                        ? modelVersions.potplant
                        : modelVersions.substraat}
                </SwitchModelLink>
            </RightBlock>
        </MainContainer>
    );
};

export default Header;
